import { ILoggingService, IConnectionMonitorService, ConnectionStatus, LiveEvents, LiveEventsCountResponse, devLogService, cashOutDtos, chatDtos } from 'eTip--common';
import { CancelError, ApiError } from './apiBase';
import { InsertMessageOperationResult, IChatPerson, IBlockedPerson, IChatSubscribeResponse } from '../dtos/chatDtos';

import 'ms-signalr-client-jquery-3'; // to bundle the script

export interface INotificationHandler<T> {
    (params: T): void;
}

export interface IHandlerToInvoke<T> {
    handler: (params: T) => void;
    data?: any;
}

interface ICommonHandlers<T> { [messageKey: string]: INotificationHandler<T>[] };

interface IEventDetailHandlers { [id: number]: INotificationHandler<{ eventId: number, eventData: LiveEvents.EventsItem }>[] };

export interface IChatMessagesChangeData { messagesAll: chatDtos.IChatMessage[], messagesUser: chatDtos.IChatMessage[] };

const enum ConnectionState {
    Connecting = 0,
    Connected = 1,
    Reconnecting = 2,
    Disconnected = 4
}

export const enum NotificationHandlerKeys { /*EventDetailChanged = "eventDetailChanged",*/ CashOutChanged = "cashOutChanged", ShopShowChanged = "shopShowChanged", ChatMessages = "chatMessages", StopChat = "stopChat", LiveEventsCountChanged = "liveEventsCountChanged" };

abstract class PromiseCallbacks {
    public isProcessed: boolean;

    private customResolve;
    private customReject;

    public constructor(resolve: any, reject: any) {
        this.isProcessed = false;
        this.customResolve = resolve;
        this.customReject = reject;
    }

    public resolve = (data: any) => {
        if (this.isProcessed) {
            this.callHandlerIfProcessed(data);
            return;
        }

        this.isProcessed = true;
        this.customResolve(data);
    }

    protected abstract callHandlerIfProcessed(data: any): void;

    public reject = (reason: any) => {
        if (this.isProcessed) {
            return;
        }
        this.isProcessed = true;
        this.customReject(reason);
    }
};

class SimplePromiseCallbacks extends PromiseCallbacks {

    public constructor(resolve: any, reject: any) {

        super(resolve, reject);
    }

    protected callHandlerIfProcessed(_data: any) {
    }
}

class CommonPromiseCallbacks<T> extends PromiseCallbacks {

    private handlerKey: string;
    private handlers: ICommonHandlers<T>;

    public constructor(handlerKey: string, handlers: ICommonHandlers<T>, resolve: T, reject: any) {

        super(resolve, reject);
        this.handlerKey = handlerKey;
        this.handlers = handlers;
    }

    protected callHandlerIfProcessed(data: T) {
        if (this.handlers[this.handlerKey]) {
            this.handlers[this.handlerKey].forEach(item => item(data));
        }
    }
}

class ChatBroadcastPromiseCallbacks extends PromiseCallbacks {

    private handlerKey: string;
    private handlers: ICommonHandlers<IChatMessagesChangeData>;

    public constructor(handlerKey: string, handlers: ICommonHandlers<IChatMessagesChangeData>, resolve: INotificationHandler<IChatSubscribeResponse>, reject: any) {

        super(resolve, reject);
        this.handlerKey = handlerKey;
        this.handlers = handlers;
    }

    protected callHandlerIfProcessed(data: IChatSubscribeResponse) {
        if (this.handlers[this.handlerKey]) {
            let dataToSend: IChatMessagesChangeData = { messagesAll: data?.Messages, messagesUser: null }; // see chatList setDataCommon, in .Messages there are all messages
            this.handlers[this.handlerKey].forEach(item => item(dataToSend));
        }
    }
}

class EventDetailPromiseCallbacks extends PromiseCallbacks {

    private eventId: number;
    private handlers: IEventDetailHandlers;

    public constructor(eventId: number, handlers: IEventDetailHandlers, resolve: any, reject: any) {

        super(resolve, reject);
        this.eventId = eventId;
        this.handlers = handlers;
    }

    protected callHandlerIfProcessed(data: any) {
        // data has to be LiveEvents.EventsItem
        if (this.handlers[this.eventId]) {
            let dataToSend = { eventId: this.eventId, eventData: data };
            this.handlers[this.eventId].forEach(item => item(dataToSend));
        }
    }
}

export class SRNotificationHandler {
    private readonly subscriptionTimeoutMs = 20000;
    private handlers: ICommonHandlers<any> = {};
    private handlersForEventDetail: IEventDetailHandlers = {};
    private liveHub: SignalR.Hub.Proxy;
    private chatHub: SignalR.Hub.Proxy;
    private apiLanguageId: number = null;
    private apiToken: string = '';
    private state: ConnectionState = ConnectionState.Disconnected;
    private connectionMonitorConnStatus: ConnectionStatus = ConnectionStatus.Unknown;
    private hubConnection: SignalR.Hub.Connection;
    private loggingService: ILoggingService;

    private requestedEventDetailSubscriptions: { [eventId: number]: PromiseCallbacks } = {};
    private requestedEventDetailUnsubscriptions: { [eventId: number]: PromiseCallbacks } = {};
    private requestedCashoutSubscription: PromiseCallbacks = null;
    private requestedLiveEventsCountSubscription: PromiseCallbacks = null;
    private requestedTvLiveEventsSubscription: PromiseCallbacks = null;
    private requestedChatMessagesSubscription: PromiseCallbacks = null;

    private canUseChatHub: boolean;

    private readonly canLogInfoAboutEventDetailSubscribe = false;

    constructor(canUseChatHub: boolean) {
        this.canUseChatHub = canUseChatHub;
    }

    public startSubscribing = (apiUrl: string, token: string, languageId: number, canDisableSignalRSSE: boolean | null, connectionMonitorService: IConnectionMonitorService, loggingService: ILoggingService): void => {
        this.handlers = {};
        this.handlersForEventDetail = {};
        this.apiLanguageId = languageId;
        this.apiToken = token;
        this.loggingService = loggingService;
        const url = `${apiUrl}/signalr`;

        connectionMonitorService.subscribeConnectionStatusChange((newConnectionStatus: ConnectionStatus) => {
            devLogService.log("Signalr Status " + (newConnectionStatus.toString()));
            this.connectionMonitorConnStatus = newConnectionStatus;
            if (this.state == ConnectionState.Disconnected && this.connectionMonitorConnStatus == ConnectionStatus.Connected) {
                this.reconnectSignalRAsync();
            }
        });

        this.hubConnection = $.hubConnection(url, { useDefaultPath: false, logging: false });

        this.hubConnection.stateChanged((states: { oldState: number; newState: number }) => {
            devLogService.log('SignalR: stateChanged to ' + states.newState);
            loggingService.info('SignalR: stateChanged', states);
            this.state = states.newState;
        });

        this.hubConnection.disconnected(() => {
            devLogService.log('SignalR: disconected');
            loggingService.info('SignalR: disconected');
            this.state = ConnectionState.Disconnected;

            this.tryReconnectAfterSomeTimeAsync();
        });

        this.hubConnection.reconnecting(() => {
            devLogService.log('SignalR: reconnecting');
            loggingService.info('SignalR: reconnecting');
        });

        this.hubConnection.reconnected(() => {
            devLogService.log('SignalR: reconnected');
            loggingService.info('SignalR: reconnected');
        });

        this.hubConnection.connectionSlow(() => {
            loggingService.info('SignalR: We are currently experiencing difficulties with the connection.');
        });

        this.liveHub = this.hubConnection.createHubProxy('livehub');
        if (this.canUseChatHub) {
            this.chatHub = this.hubConnection.createHubProxy('chathub');
        }

        this.liveHub.on("eventDetailChanged", (_languageId, eventId, eventData) => {
            let handlers = this.handlersForEventDetail[eventId];
            if (handlers) {
                handlers.forEach(h => h({ eventId: eventId, eventData: eventData }));
            }
        });

        this.liveHub.on('cashOutChanged', data => {
            if (this.handlers[NotificationHandlerKeys.CashOutChanged]) {
                this.handlers[NotificationHandlerKeys.CashOutChanged].forEach(h => h(data));
            }
        });

        this.liveHub.on('shopShowChanged', (_languageId: number, data: any /* TODO data type +  different language may come ? */) => {
            if (this.handlers[NotificationHandlerKeys.ShopShowChanged]) {
                this.handlers[NotificationHandlerKeys.ShopShowChanged].forEach(h => h(/*languageId, */data));
            }
        });

        if (this.canUseChatHub) {
            this.chatHub.on("broadcastMessages", (messagesAll, messagesUser) => {
                if (this.handlers[NotificationHandlerKeys.ChatMessages]) {
                    this.handlers[NotificationHandlerKeys.ChatMessages].forEach(h => h(/*languageId, */ { messagesAll: messagesAll, messagesUser: messagesUser }));
                }
            });

            this.chatHub.on("stopChat", () => {
                if (this.handlers[NotificationHandlerKeys.StopChat]) {
                    this.handlers[NotificationHandlerKeys.StopChat].forEach(h => h(/*languageId, */{}));
                }
            });

        }

        if (__IsMTSTipos__) {
            this.liveHub.on('liveEventsCountChanged', (data: LiveEventsCountResponse) => {
                if (this.handlers[NotificationHandlerKeys.LiveEventsCountChanged]) {
                    this.handlers[NotificationHandlerKeys.LiveEventsCountChanged].forEach(h => h(data));
                }
            });
        }

        let options = {
            withCredentials: false,
            transport: canDisableSignalRSSE ? [/* do not allow webSockets, blocked by F5, czech prod
                 'webSockets', */'serverSentEvents', 'foreverFrame', 'longPolling'] : undefined
        };
        this.hubConnection   //see also reconnectSignalR
            .start(options) //also reconnectSignalRAsync
            .done(() => {
                loggingService.info('SignalR: Now connected, connection ID=' + this.hubConnection.id);
                this.invokeRegisteredHandlersAfterConnect();
            })
            .fail(() => {
                loggingService.error('SignalR: Could not connect');
                this.tryReconnectAfterSomeTimeAsync();
            });
    };

    private reconnectSignalRAsync = (): void => {
        this.loggingService.info('SignalR: Trying reconnect after disconnect. this.state=', this.state);
        this.hubConnection.start({ withCredentials: false } /* also above */).done(() => {
            this.loggingService.info('SignalR: Now reconnected, connection ID=' + this.hubConnection.id);
            this.invokeRegisteredHandlersAfterConnect();
        }).fail((error) => {
            this.loggingService.warning('SignalR: Error when reconnecting ' + error);
            this.tryReconnectAfterSomeTimeAsync();
        });
    };

    private tryReconnectAfterSomeTimeAsync = (): void => {
        // if connectionMonitor is connected, try to reconnect. If not, do nothing until connectionMonitor is connected in subscribeConnectionStatusChange
        if (this.state == ConnectionState.Disconnected && this.connectionMonitorConnStatus == ConnectionStatus.Connected) {
            window.setTimeout(() => {
                if (this.state == ConnectionState.Disconnected && this.connectionMonitorConnStatus == ConnectionStatus.Connected) {
                    this.reconnectSignalRAsync();
                }
            }, 10000);
        }
    }

    public subscribeForEventDetailAsync = (eventId: number, callback: INotificationHandler<{ eventId: number, eventData: LiveEvents.EventsItem }>): Promise<LiveEvents.EventsItem | null> => {

        if (this.canLogInfoAboutEventDetailSubscribe) {
            this.loggingService.info("sRNotification.subscribeForEventDetailAsync " + eventId);
        }

        // if (eventId === 1122334455) { //TODOHM remove, for mobile app development
        //     return new Promise((resolve, _reject) => {
        //         window.setTimeout(() => {
        //             resolve(/* !!!hard cast */<LiveEvents.EventsItem>{
        //                 ID: 1122334455,
        //                 Name: "Demo1 - Demo2",
        //                 DisciplineID: 1,
        //                 State: 3,
        //                 StateTime: null,
        //                 Competitors: [{ ID: 1, Name: "P1", IsServer: false }, { ID: 2, Name: "P2", IsServer: false }],
        //                 Actions: [],
        //                 Results: [],
        //                 GameGroups: [],
        //                 ExternalID: "22421657",

        //                 StateName: "Demo",
        //                 StreamTv: { ID: "1", Provider: 2, StartDate: null }
        //             });
        //         }, 100);
        //     });
        // }

        let existingUnsubscription = this.requestedEventDetailUnsubscriptions[eventId]; //remove old unsubscriptions, now I want to subscribe again
        if (existingUnsubscription) {
            delete this.requestedEventDetailUnsubscriptions[eventId];
            existingUnsubscription.reject(new CancelError());
        }

        var existing = this.requestedEventDetailSubscriptions[eventId];
        if (existing) {
            this.loggingService.warning("Another subscription exists for " + eventId);
            existing.reject(new CancelError());
        }

        let promiseCallbacks: PromiseCallbacks = null;
        let promise = new Promise<LiveEvents.EventsItem>((resolve, reject) => {
            promiseCallbacks = new EventDetailPromiseCallbacks(eventId, this.handlersForEventDetail, (data: any) => {
                resolve(data);
            },
                (reason: any) => {
                    reject(reason);
                });
            return promiseCallbacks;
        });

        this.requestedEventDetailSubscriptions[eventId] = promiseCallbacks;

        if (this.state == ConnectionState.Connected) {
            setTimeout(() => {
                this.subscribeForEventDetailRaw(eventId, promiseCallbacks);
            }, 1);
        } else {
            setTimeout(() => {
                if (!promiseCallbacks.isProcessed) {
                    promiseCallbacks.reject(this.createTimeoutError());
                }
                // it may be fulfilled in invokeRegisteredHandlersAfterConnect
            }, this.subscriptionTimeoutMs);
        }

        this.registerNotificationHandlerForEventDetail(eventId, callback);

        return promise;
    }

    private subscribeForEventDetailRaw = (eventId: number, callbacks: PromiseCallbacks): void => {
        if (this.canLogInfoAboutEventDetailSubscribe) {
            this.loggingService.info("sRNotification.subscribeForEventDetailRaw " + eventId);
        }
        this.liveHub
            .invoke("subscribeToEventDetail", this.apiLanguageId, this.apiToken, eventId)
            .done(callbacks.resolve)
            .fail((_error) => {
                //this.loggingService.error('TCL: Invocation of subscribeForEventDetail failed. Error: ' + _error);
                callbacks.reject(_error);
            });
    }


    private openingEventDetails: Map<number, number> = new Map<number, number>();  // eventId => number of openings

    public openingEventDetail = (eventId: number) => {

        if (this.openingEventDetails.has(eventId)) {
            let newNumber = this.openingEventDetails.get(eventId) + 1;
            this.openingEventDetails.set(eventId, newNumber);
            if (this.canLogInfoAboutEventDetailSubscribe) {
                this.loggingService.info("sRNotification.openingEventDetail " + eventId + " " + newNumber);
            }
            return;
        }
        this.openingEventDetails.set(eventId, 1);
        if (this.canLogInfoAboutEventDetailSubscribe) {
            this.loggingService.info("sRNotification.openingEventDetail " + eventId + " 1");
        }
    }

    public closingEventDetail = (eventId: number) => {
        if (this.openingEventDetails.has(eventId)) {
            let v = this.openingEventDetails.get(eventId);
            if (v > 1) {
                this.openingEventDetails.set(eventId, v - 1);
                if (this.canLogInfoAboutEventDetailSubscribe) {
                    this.loggingService.info("sRNotification.closingEventDetail " + eventId + " " + (v - 1));
                }
                return;
            }
            this.openingEventDetails.delete(eventId);
            if (this.canLogInfoAboutEventDetailSubscribe) {
                this.loggingService.info("sRNotification.closingEventDetail " + eventId + " remove");
            }
        }
    }

    public unsubscribeFromEventDetailAsync = (eventId: number): Promise<any> => {
        /*
        There are two scenarios with possible use subscription to the same event
        1) desktop live, event list with event detail -> user clicks on event name and goes to detail only: subscription in mount of detail only is called before unmount with unsubscribe.
        2) mobile web - banner has subscribed live event and the same is opened from ticket.

        */

        if (this.canLogInfoAboutEventDetailSubscribe) {
            this.loggingService.info("sRNotification.unsubscribeFromEventDetailAsync " + eventId);
        }

        if (!this.openingEventDetails.has(eventId)) {
            let existingSubscription = this.requestedEventDetailSubscriptions[eventId];  //remove old subscriptions, now I want to unsubscribe again
            if (existingSubscription) {
                delete this.requestedEventDetailSubscriptions[eventId];
                existingSubscription.reject(new CancelError());
            }
        }

        // in fact if there is another in openingEventDetails, it's may be problem with unsubscribing due to order of operations. But what in case of error during subscribe? => skip unsubscribe below


        let existingUnsubscription = this.requestedEventDetailUnsubscriptions[eventId];
        if (existingUnsubscription) {
            delete this.requestedEventDetailUnsubscriptions[eventId];
            existingUnsubscription.reject(new CancelError());
        }

        let promiseCallbacks: PromiseCallbacks = null;
        let promise = new Promise((resolve, reject) => {
            promiseCallbacks = new SimplePromiseCallbacks((data: any) => {
                delete this.requestedEventDetailUnsubscriptions[eventId];
                resolve(data);
            },
                (reason: any) => {
                    delete this.requestedEventDetailUnsubscriptions[eventId];
                    reject(reason);
                });
            return promiseCallbacks;
        });

        this.requestedEventDetailUnsubscriptions[eventId] = promiseCallbacks;

        if (this.state == ConnectionState.Connected) {
            setTimeout(() => {
                if (this.openingEventDetails.has(eventId)) {
                    if (this.canLogInfoAboutEventDetailSubscribe) {
                        this.loggingService.info("sRNotification.unsubscribeFromEventDetailAsync " + eventId + " skipping");
                    }
                    promiseCallbacks.resolve(null); // there is another to subscribe, do nothing
                    return;
                }
                this.unsubscribeFromEventDetailRaw(eventId, promiseCallbacks);
            }, 1);
        } else {
            setTimeout(() => {
                if (!promiseCallbacks.isProcessed) {
                    promiseCallbacks.reject(this.createTimeoutError());
                }
                // it may be fulfilled in invokeRegisteredHandlersAfterConnect
            }, this.subscriptionTimeoutMs);
        }
        return promise;
    }

    private unsubscribeFromEventDetailRaw = (eventId: number, callbacks: PromiseCallbacks): void => {
        if (this.canLogInfoAboutEventDetailSubscribe) {
            this.loggingService.info("sRNotification.unsubscribeFromEventDetailRaw " + eventId);
        }
        this.liveHub
            .invoke("unsubscribeFromEventDetail", this.apiLanguageId, this.apiToken, eventId)
            .done(callbacks.resolve)
            .fail((_error) => {
                //this.loggingService.error('TCL: Invocation of unsubscribeFromEventDetail failed. Error: ' + _error);
                callbacks.reject(_error);
            });
    }

    public subscribeForTvLiveEventsListAsync = (callback: INotificationHandler<object>): Promise<LiveEvents.ReturnValueItem[]> => {
        if (this.requestedTvLiveEventsSubscription) {
            this.requestedTvLiveEventsSubscription.reject(new CancelError());
            this.requestedTvLiveEventsSubscription = null;
        }

        let promiseCallbacks: PromiseCallbacks = null;
        let promise = new Promise<LiveEvents.ReturnValueItem[]>((resolve, reject) => {
            promiseCallbacks = new CommonPromiseCallbacks(NotificationHandlerKeys.ShopShowChanged, this.handlers, (data: LiveEvents.ReturnValueItem[]) => {
                resolve(data);
            },
                (reason: any) => {
                    reject(reason);
                });
            return promiseCallbacks;
        });
        this.requestedTvLiveEventsSubscription = promiseCallbacks;

        if (this.state == ConnectionState.Connected) {
            setTimeout(() => {
                this.subscribeForTVEventListRaw(promiseCallbacks);
            }, 1);
        }
        else {
            setTimeout(() => {
                if (!promiseCallbacks.isProcessed) {
                    promiseCallbacks.reject(this.createTimeoutError());
                }
                // it may be fulfilled in invokeRegisteredHandlersAfterConnect
            }, this.subscriptionTimeoutMs);
        }

        this.registerOneNotificationHandlerForKey(NotificationHandlerKeys.ShopShowChanged, callback);

        return promise;
    }

    public subscribeForTVEventListRaw = (callbacks: PromiseCallbacks): void => {
        this.liveHub
            .invoke("SubscribeToShopShow", this.apiLanguageId, this.apiToken)
            .done(callbacks.resolve)
            .fail((_error) => {
                //this.loggingService.error('TCL: Invocation of subscribeForEventDetail failed. Error: ' + _error);
                callbacks.reject(_error);
            });
    }

    public subscribeForCashoutAsync = (callback: INotificationHandler<cashOutDtos.CashOutResponse>): Promise<cashOutDtos.CashOutResponse> => {
        if (this.requestedCashoutSubscription) {
            this.requestedCashoutSubscription.reject(new CancelError());
            this.requestedCashoutSubscription = null;
        }

        let promiseCallbacks: PromiseCallbacks = null;
        let promise = new Promise<cashOutDtos.CashOutResponse>((resolve, reject) => {
            promiseCallbacks = new CommonPromiseCallbacks(NotificationHandlerKeys.CashOutChanged, this.handlers, (data: cashOutDtos.CashOutResponse) => {
                resolve(data);
            },
                (reason: any) => {
                    reject(reason);
                });
        });
        this.requestedCashoutSubscription = promiseCallbacks;

        if (this.state == ConnectionState.Connected) {
            setTimeout(() => {
                this.subscribeForCashoutRaw(promiseCallbacks);
            }, 1);
        } else {
            setTimeout(() => {
                if (!promiseCallbacks.isProcessed) {
                    promiseCallbacks.reject(this.createTimeoutError());
                }
                // it may be fulfilled in invokeRegisteredHandlersAfterConnect
            }, this.subscriptionTimeoutMs);
        }

        this.registerOneNotificationHandlerForKey(NotificationHandlerKeys.CashOutChanged, callback);

        return promise;
    }

    public subscribeForLiveEventsCountAsync = (callback: INotificationHandler<object>): Promise<LiveEventsCountResponse> => {
        if (this.requestedLiveEventsCountSubscription) {
            this.requestedLiveEventsCountSubscription.reject(new CancelError());
            this.requestedLiveEventsCountSubscription = null;
        }

        let promiseCallbacks: PromiseCallbacks = null;
        let promise = new Promise<LiveEventsCountResponse>((resolve, reject) => {
            promiseCallbacks = new CommonPromiseCallbacks(NotificationHandlerKeys.LiveEventsCountChanged, this.handlers, (data: LiveEventsCountResponse) => {
                resolve(data);
            },
                (reason: any) => {
                    reject(reason);
                });
        });
        this.requestedLiveEventsCountSubscription = promiseCallbacks;

        if (this.state == ConnectionState.Connected) {
            setTimeout(() => {
                this.subscribeForLiveEventsCountRaw(promiseCallbacks);
            }, 1);
        } else {
            setTimeout(() => {
                if (!promiseCallbacks.isProcessed) {
                    promiseCallbacks.reject(this.createTimeoutError());
                }
                // it may be fulfilled in invokeRegisteredHandlersAfterConnect
            }, this.subscriptionTimeoutMs);
        }

        this.registerOneNotificationHandlerForKey(NotificationHandlerKeys.LiveEventsCountChanged, callback);

        return promise;
    }

    private subscribeForCashoutRaw = (callbacks: PromiseCallbacks): void => {
        this.liveHub
            .invoke("subscribeToCashOut", this.apiLanguageId, this.apiToken)
            .done(callbacks.resolve)
            .fail((error) => {
                //this.loggingService.error('TCL: Invocation of subscribeForCashout failed. Error: ' + error);
                callbacks.reject(error);
            });
    }

    private subscribeForLiveEventsCountRaw = (callbacks: PromiseCallbacks): void => {
        this.liveHub
            .invoke("subscribeToLiveEventsCount", this.apiLanguageId)
            .done(callbacks.resolve)
            .fail((error) => {
                callbacks.reject(error);
            });
    }

    public subscribeForChatMessagesAsync = (callbackForChange: INotificationHandler<IChatMessagesChangeData>): Promise<IChatSubscribeResponse> => {
        if (this.requestedChatMessagesSubscription) {
            this.requestedChatMessagesSubscription.reject(new CancelError());
            this.requestedChatMessagesSubscription = null;
        }

        let promiseCallbacks: PromiseCallbacks = null;
        let promise = new Promise<IChatSubscribeResponse>((resolve, reject) => {
            promiseCallbacks = new ChatBroadcastPromiseCallbacks(NotificationHandlerKeys.ChatMessages, this.handlers, (data: IChatSubscribeResponse) => {
                resolve(data);
            },
                (reason: any) => {
                    reject(reason);
                });
            return promiseCallbacks;
        });
        this.requestedChatMessagesSubscription = promiseCallbacks;

        if (this.state == ConnectionState.Connected) {
            setTimeout(() => {
                this.subscribeForChatMessagesRaw(promiseCallbacks);
            }, 1);
        }
        else {
            setTimeout(() => {
                if (!promiseCallbacks.isProcessed) {
                    promiseCallbacks.reject(this.createTimeoutError());
                }
                // it may be fulfilled in invokeRegisteredHandlersAfterConnect
            }, this.subscriptionTimeoutMs);
        }

        this.registerOneNotificationHandlerForKey(NotificationHandlerKeys.ChatMessages, callbackForChange);

        return promise;
    }

    public subscribeForChatMessagesRaw = (callbacks: PromiseCallbacks): void => {
        this.chatHub
            .invoke("subscribeToChatMessages", this.apiLanguageId, this.apiToken)
            .done(callbacks.resolve)
            .fail((_error) => {
                callbacks.reject(_error);
            });
    }

    public sendChatMessageAsync = (text: string): Promise<InsertMessageOperationResult> => {
        let promise = new Promise<InsertMessageOperationResult>((resolve, reject) => {

            if (this.state != ConnectionState.Connected) {
                reject();
            }

            this.chatHub
                .invoke("SendMessage", this.apiLanguageId, this.apiToken, text)
                .done(resolve)
                .fail((error) => {
                    reject(error);
                });
        });

        return promise;
    }

    public sendChatReplyMessageAsync = (text: string, replyMessageId: string, isPrivate: boolean): Promise<InsertMessageOperationResult> => {
        let promise = new Promise<InsertMessageOperationResult>((resolve, reject) => {

            if (this.state != ConnectionState.Connected) {
                reject();
            }

            this.chatHub
                .invoke("SendReplyMessage", this.apiLanguageId, this.apiToken, text, replyMessageId, isPrivate)
                .done(resolve)
                .fail((error) => {
                    reject(error);
                });
        });

        return promise;
    }

    public setBlockAllPrivateAsync = (isBlocked: boolean): Promise<IChatPerson> => {
        let promise = new Promise<IChatPerson>((resolve, reject) => {

            if (this.state != ConnectionState.Connected) {
                reject();
            }

            this.chatHub
                .invoke("SetBlockAllPrivate", this.apiLanguageId, this.apiToken, isBlocked)
                .done(resolve)
                .fail((error) => {
                    reject(error);
                });
        });

        return promise;
    }

    public setBlockedPersonAsync = (personId: number, isBlocked: boolean): Promise<IBlockedPerson[]> => {
        let promise = new Promise<IBlockedPerson[]>((resolve, reject) => {

            if (this.state != ConnectionState.Connected) {
                reject();
            }

            this.chatHub
                .invoke("BlockOrUnblockPerson", this.apiLanguageId, this.apiToken, personId, isBlocked)
                .done(resolve)
                .fail((error) => {
                    reject(error);
                });
        });

        return promise;
    }

    //blockOrUnblockPerson

    /**
     * Registers handler to get changes = to be notified about changes from server.
     */
    public registerOneNotificationHandlerForKey = (messageKey: NotificationHandlerKeys, handler: INotificationHandler<Object>): void => {
        this.handlers[messageKey] = [handler];
    };

    public registerNotificationHandlerForEventDetail = (eventId: number, handler: INotificationHandler<{ eventId: number, eventData: LiveEvents.EventsItem }>): void => {
        let handlers = this.handlersForEventDetail[eventId];
        if (handlers) {
            handlers.push(handler);
            return;
        }
        this.handlersForEventDetail[eventId] = [handler];
    };

    public unregisterNotificationHandler = (messageKey: NotificationHandlerKeys, handler: INotificationHandler<Object>): void => {
        if (this.handlers[messageKey]) {
            this.handlers[messageKey] = this.handlers[messageKey].filter(h => h !== handler);
        }
    };

    public unregisterNotificationHandlerForEventDetail = (eventId: number, handler: INotificationHandler<Object>): void => {
        let handlers = this.handlersForEventDetail[eventId];
        if (handlers) {
            let pos = handlers.indexOf(handler);
            handlers.splice(pos, 1);
            if (handlers.length == 0) {
                this.handlersForEventDetail[eventId] = undefined;
            }
        }
    }

    private invokeRegisteredHandlersAfterConnect(): void {
        Object.keys(this.requestedEventDetailUnsubscriptions).forEach((eventIdAsString: string) => {
            let eventId = Number(eventIdAsString);
            let promiseCallbacks = this.requestedEventDetailUnsubscriptions[eventId];
            this.unsubscribeFromEventDetailRaw(Number(eventId), promiseCallbacks);
        });

        Object.keys(this.requestedEventDetailSubscriptions).forEach((eventIdAsString: string) => {
            let eventId = Number(eventIdAsString);
            let promiseCallbacks = this.requestedEventDetailSubscriptions[eventId];
            this.subscribeForEventDetailRaw(Number(eventId), promiseCallbacks);
        });

        if (this.requestedCashoutSubscription) {
            this.subscribeForCashoutRaw(this.requestedCashoutSubscription);
        }

        if (this.requestedLiveEventsCountSubscription) {
            this.subscribeForLiveEventsCountRaw(this.requestedLiveEventsCountSubscription);
        }

        if (this.requestedTvLiveEventsSubscription) {
            this.subscribeForTVEventListRaw(this.requestedTvLiveEventsSubscription);
        }

        if (this.requestedChatMessagesSubscription) {
            this.subscribeForChatMessagesRaw(this.requestedChatMessagesSubscription);
        }
    }

    private createTimeoutError(): ApiError {
        return new ApiError("Still not connected, timeout for waiting to reconnect")
    }
}

